<template>
  <div class="bg-picker">
    <div
      class="container xxl:px-24 md:px-56 px-0 md:py-56 py-24 flex md:flex-row flex-col"
    >
      <!--      <DebugOutput>{{ receipt }}</DebugOutput>-->

      <!-- * * * * left column * * *  -->
      <div class="md:w-3/5 md:mr-24 mb-24">
        <div
          ref="el"
          class="md:p-32 px-24 py-32 w-full bg-white md:rounded-xl"
        >
          <div v-if="paymentMethod.startsWith('klarna')" ref="kcoHolder" />
          <div v-if="paymentMethod === 'seller'">
            <div class="text-heading-small">
              {{ $lang('checkout', 'seller_thank_you_heading') }}
            </div>
            <div>{{ $lang('checkout', 'thankyou_order_number') }}: #{{ receipt?.order.order }}</div>
            <div>{{ $lang('checkout', 'thankyou_total') }}: {{ receipt?.order.totals.grandTotalPrice }}</div>
          </div>
          <div v-if="paymentMethod === 'paypal'">
            <div class="text-heading-large mb-16">
              {{ $lang('checkout', 'paypal_thank_you_heading') }}
            </div>
            <div class="text-small mb-4">
              {{ paypalThankYouText }}
            </div>
            <div class="text-small mb-4">
              {{ order?.items[0].name }}
            </div>
            <div v-if="payPalPrice" class="text-small">{{ payPalPrice }}</div>
          </div>
        </div>
      </div>
      <!-- * * * * /left column * * *  -->

      <!-- * * * * right column * * *  -->
      <div v-if="paymentMethod !== 'paypal'" class="md:w-2/5">
        <div class="flex flex-col justify-center md:p-32 px-24 py-32 w-full bg-white md:rounded-xl">
          <div v-if="paymentMethod.startsWith('klarna') || paymentMethod === 'seller'">
            <div class="text-heading-small mb-16">
              {{ $lang('checkout', 'thank_you_heading') }}
            </div>
            <div class="text-small" v-html="donationText" />
          </div>
        </div>
      </div>
      <!-- * * * * /right column * * *  -->
    </div>
  </div>
</template>

<script setup lang="ts">
import useOrderComplete, { type PaymentResultResponse } from '~/composables/useOrderComplete';
import useApiFetch from '~/composables/useApiFetch';
import { getData } from 'nuxt-storage/local-storage';
import OrderItem from '~/models/order';
import type { SellerClub, SellerPerson } from '~/constants/types/seller';
import { useCartStore } from '~/store/cart';
const cartStore = useCartStore();
const invoiceRejected = useCookie<boolean>('invoiceRejected', { maxAge: 3600 * 24 * 182, path: '/', default: () => false });

const { createPreparedShipment, sendGtmReceipt, setNewsletter } = useOrderComplete();
const clubCookie = useCookie<SellerClub>('club', { maxAge: 3600 * 24 * 182, path: '/' });
const sellerCookie = useCookie<SellerPerson>('seller', { maxAge: 3600 * 24 * 182, path: '/' });
const { $lang } = useNuxtApp();

const { centraGet, lastCentraErrors } = useApiFetch();

const receipt = ref<PaymentResultResponse | null>(null);
const order = ref<OrderItem | null>(null);
const paymentMethod = ref('');

const kcoHolder = ref<HTMLDivElement | null>(null);

const { data } = await useAsyncData('thankYouContentLoad', async() => {
  const res = await centraGet<PaymentResultResponse>('/receipt');
  if (res) {
    return res;
  } else {
    console.error();
    console.log(lastCentraErrors.value);
  }
});

if (data.value) {
  receipt.value = data.value;
  paymentMethod.value = data.value.order.paymentMethod;
  order.value = new OrderItem(data.value.order);
} else {
  console.error();
}

onMounted(() => {
  cartStore.initialCart();
  invoiceRejected.value = false;
  const kcoScript = getData('kcoResult');
  if (paymentMethod.value.startsWith('klarna') && kcoHolder.value && kcoScript) {
    injectKco(kcoScript);
  }
  if (paymentMethod.value.startsWith('klarna') && order.value) {
    createPreparedShipment(order.value);
  }
  if (order.value) {
    sendGtmReceipt(order.value);
  }
  setNewsletter();
});

const injectKco = (snippet: string) => {
  if (kcoHolder.value) {
    kcoHolder.value.innerHTML = snippet;
    const scriptsTags = kcoHolder.value.getElementsByTagName('script');

    for (let i = 0; i < scriptsTags.length; i++) {
      const parentNode = scriptsTags[i].parentNode;
      const newScriptTag = document.createElement('script');
      newScriptTag.type = 'text/javascript';
      newScriptTag.text = scriptsTags[i].text;
      parentNode!.removeChild(scriptsTags[i]);
      parentNode!.appendChild(newScriptTag);
    }
  }
};

const donationText = computed(()=> {
  const donation =
    (order.value &&
      (
        (order.value.totalPriceAsNumber - order.value.shippingPriceAsNumber) /
        10
      ).toFixed(0)) ||
    0;

  /**
   * @todo - translations
   */
  if (clubCookie.value && sellerCookie.value) {
    return $lang('header', 'club_site_message_v2', {
      club: '<span class="font-medium">' + clubCookie.value.Name +'</span>',
      seller: '<span class="font-medium">' + sellerCookie.value.Name +'</span>',
    });
  } else {
    return $lang('checkout', 'thank_you_text')
      .replace('{{amount}}', donation.toString())
      .replace('{{clubTitle}}', $lang('checkout', 'ullmax_foundation'));
  }
});

const paypalThankYouText = computed(()=> {
  return order.value
    ? $lang('checkout', 'paypal_thank_you_text').replace(
      '{{email}}',
      order.value.shippingAddress.email
    )
    : '';
});
const payPalPrice = computed(()=> {
  return order.value
    ? $lang('checkout', 'thank_you_text_paypal').replace(
      '{{price}}',
      order.value.totalPrice
    )
    : '';
});

</script>

<style scoped lang="postcss">
</style>
